import * as React from 'react'
import { Portal } from 'react-portal'

import styles from './image-renderer.module.scss'

const FullscreenImage: React.FC<
  {
    isOpen: boolean
    toggle: () => void
  } & React.ImgHTMLAttributes<HTMLImageElement>
> = ({ isOpen, toggle, ...props }) => {
  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        toggle()
      }
    }

    window.addEventListener('keyup', handler)
    document.body.classList.add(styles.noscroll)

    return () => {
      window.removeEventListener('keyup', handler)
      document.body.classList.remove(styles.noscroll)
    }
  }, [isOpen, toggle])

  if (!isOpen) {
    return null
  }

  return (
    <Portal>
      <div
        className={styles.fullscreenImageWrapper}
        onClick={() => toggle()}
        onKeyPress={(event) => (event.key === 'Enter' || event.key === ' ') && toggle()}
        role="button"
        tabIndex={0}
      >
        <div className={styles.fullscreenImageInnerWrapper}>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img {...props} className={styles.fullscreenImage} />
        </div>
      </div>
    </Portal>
  )
}

const ImageRenderer: React.FC<
  {
    nodeKey: string
    src: string
  } & React.ImgHTMLAttributes<HTMLImageElement>
> = ({ nodeKey, ...props }) => {
  const [zoomEnabled, setZoomEnabled] = React.useState(false)
  const [isOpen, setOpen] = React.useState(false)

  const interactiveProps = zoomEnabled
    ? {
        onClick: () => setOpen((value) => !value),
        onKeyPress: (event: React.KeyboardEvent) =>
          (event.key === 'Enter' || event.key === ' ') && setOpen((value) => !value),
        role: 'button',
        tabIndex: 0,
        style: {
          ...props.style,
          cursor: 'zoom-in',
        },
      }
    : {}

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        key={nodeKey}
        // enable zooming if the image natural width is "much" larger than the rendered width
        onLoad={(e) => setZoomEnabled(e.currentTarget.naturalWidth > e.currentTarget.width * 1.5)}
        {...props}
        {...interactiveProps}
      />
      <FullscreenImage isOpen={isOpen} toggle={() => setOpen((value) => !value)} {...props} />
    </>
  )
}

export default ImageRenderer
